//@flow
import HomeIcon from "@mui/icons-material/Home";
import { Tab, Tabs } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { Fragment, useContext, useEffect } from 'react';
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import ExhibitionUpdateStatus from "../../components/app/alerts/exhibitionUpdateStatus/exhibitionUpdateStatus";
import Breadcrumb from "../../components/general/breadcrumb/breadcrumb";
import ContentLoader from "../../components/general/contentLoader/contentLoader";
import CustomTranslation from "../../components/general/customTranslation/customTranslation";
import { TabPanel } from "../../components/general/tabPanel/tabPanel";
import { PartnerTypes, Permissions } from "../../helpers/constants";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { normalizeGreek } from "../../helpers/normalizeGreek";
import { useExhibition } from "../../hooks/exhibitions/useExhibition";
import { useExhibitor } from "../../hooks/exhibitions/useExhibitor";
import useQueryParams from "../../hooks/useQueryParams";
import { EXHIBITOR_PROFILE } from "../../locales/components/namespaces";
import PermissionHOC from "../../modules/permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../modules/permission/hooks/useHasPermission";
import { ROUTE_PAGE_EXHIBITION, ROUTE_PAGE_HOME, ROUTE_PAGE_PROFILE_EXHIBITOR } from "../../routers/routes";
import type { Exhibitor } from "../../types/exhibition/types";
import Partners from "../exhibition/partials/common/partners/partners";
import Visitors from "../exhibition/partials/common/visitors/visitors";
import ExhibitorKit from "../exhibition/partials/exhibitor/exhibitorKit/exhibitorKit";
import FreeAdvertisement from "../exhibition/partials/exhibitor/freeAdvertisement/freeAdvertisement";
import ScannedVisitors from "../exhibition/partials/exhibitor/scanVisitors/partials/scannedVisitors";
import ScanVisitors from "../exhibition/partials/exhibitor/scanVisitors/scanVisitors";
import WrapperWithSettingsButton from "../exhibition/partials/organizer/main/partials/wrapperWithSettingsButton";
import { TitleContext } from "../main/content";
import Catalog from "./partials/catalog";
import DisplayPageIfAllowed from "./partials/displayPageIfAllowed";

type Props = {
	exhibitor: Exhibitor,
	propsId: string,
	exhibitionId: string
}

const InnerContent = (props: Props) => {
	const { exhibitor, propsId, exhibitionId, loading, section } = props
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { setTitle, setToolbar } = useContext(TitleContext)
	const { exhibition } = useExhibition({ noFetchFiles: true })
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	const isExhibitor = useHasPermission([Permissions.BeExhibitor], { shouldView: true })
	useEffect(() => {
		if (!propsId && isOrganizer) {
			setTitle(
				<React.Fragment>
					<Typography variant="h6" component="div" sx={{ mr: 1 }}>
						{exhibitor?.name}
					</Typography>
				</React.Fragment>
			)
		}
		return () => {
			if (isOrganizer) {
				setTitle(null)
			}
		}
	}, [exhibitor?.name, isOrganizer, propsId, setTitle])
	useEffect(() => {
		if (isOrganizer) {
			setToolbar(
				<Tabs
					variant="scrollable"
					value={section}
					indicatorColor="primary"
					textColor="inherit"
				>
					<Tab
						component={Link}
						value={""}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {
							id: exhibitionId,
							exhibitorId: exhibitor.id
						}, { section: "profile" })}
						label={normalizeGreek(t("Partners"))}
					/>
					<Tab
						component={Link}
						value={"serviceProviders"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "serviceProviders"
						})}
						label={normalizeGreek(t("Service Providers"))}
					/>
					<Tab
						component={Link}
						value={"contractors"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "contractors"
						})}
						label={normalizeGreek(t("Contractors"))}
					/>
					<Tab
						component={Link}
						value={"exhibitionStaff"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {
							id: exhibitionId,
							exhibitorId: exhibitor.id
						}, {
							section: "profile",
							subSection: "exhibitionStaff"
						})}
						label={normalizeGreek(t("Exhibition Staff"))}
					/>
					<Tab
						component={Link}
						value={"organizers"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {
							id: exhibitionId,
							exhibitorId: exhibitor.id
						}, {
							section: "profile",
							subSection: "organizers"
						})}
						label={normalizeGreek(t("Organizers"))}
					/>
					<Tab
						component={Link}
						value={"visitors"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "visitors"
						})}
						label={normalizeGreek(t("Visitors"))}
					/>
					<Tab
						component={Link}
						value={"catalog"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "catalog"
						})}
						label={normalizeGreek(t("Catalogue"))}
					/>
					
					<Tab
						component={Link}
						value={"free-advertisement"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "free-advertisement"
						})}
						label={normalizeGreek(t("Free Catalogue Advertisement"))}
					/>
					<Tab
						component={Link}
						value={"kit"}
						to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, { id: exhibitionId, exhibitorId: exhibitor.id }, {
							section: "profile",
							subSection: "kit"
						})}
						label={normalizeGreek(t("Marketing Kit"))}
					/>
					{!isOrganizer && (
						<Tab
							component={Link}
							value={"scanVisitors"}
							to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {
								id: exhibitionId,
								exhibitorId: exhibitor.id
							}, {
								section: "profile",
								subSection: "scanVisitors"
							})}
							label={normalizeGreek(t("Scan Visitors"))}
						/>
					)}
					{!isOrganizer && (
						<Tab
							component={Link}
							value={"myVisitors"}
							to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {
								id: exhibitionId,
								exhibitorId: exhibitor.id
							}, {
								section: "profile",
								subSection: "myVisitors"
							})}
							label={normalizeGreek(t("My Visitors"))}
						/>
					)}
					{/*<Tab*/}
					{/*    component={Link}*/}
					{/*    value={"misc"}*/}
					{/*    to={getRouteUrl(ROUTE_PAGE_PROFILE_EXHIBITOR, {id: exhibitionId, exhibitorId: exhibitor.id}, {*/}
					{/*        section: "profile",*/}
					{/*        subSection: "misc"*/}
					{/*    })}*/}
					{/*    label={normalizeGreek(t("Misc Information"))}*/}
					{/*/>*/}
				</Tabs>
			)
		}
		return () => {
			if (isOrganizer) {
				setToolbar(null)
			}
		}
	}, [isOrganizer, setToolbar, t, section, exhibitionId, propsId, exhibitor.id])
	return (
		<Grid container spacing={2} sx={{ display: 'flex' }}>
			<Grid size={{ xs: 12, md: 12 }}>
				{exhibition && isOrganizer && (
					<Breadcrumb>
						<Breadcrumb.Link iconComponent={HomeIcon} to={getRouteUrl(ROUTE_PAGE_HOME)}>
							{t('Home')}
						</Breadcrumb.Link>
						<Breadcrumb.Link to={getRouteUrl(ROUTE_PAGE_EXHIBITION, { id: exhibition.id })}>
							{exhibition.title}
						</Breadcrumb.Link>
						<Breadcrumb.Link
							to={getRouteUrl(ROUTE_PAGE_EXHIBITION, { id: exhibition.id }, { section: "exhibitors" })}>
							{t('Exhibitors')}
						</Breadcrumb.Link>
						<Breadcrumb.Item notFocused>
							{exhibitor.name}
						</Breadcrumb.Item>
						<Breadcrumb.Item>
							{t(
								section === "" ? "Partners" :
									section === "serviceProviders" ? "Service Providers" :
										section === "contractors" ? "Contractors" :
											section === "exhibitionStaff" ? "Exhibition Staff" :
												section === "organizers" ? "Organizers" :
													section === "visitors" ? "Visitors" :
														section === "catalog" ? "Catalogue" :
															section === "kit" ? "Marketing Kit" :
																section === "misc" ? "Misc Information" : ""
							)}
						</Breadcrumb.Item>
					</Breadcrumb>
				)}
				<TabPanel isVisible={section === ""}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayPartnersToExhibitor)}>
						<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
							<CustomTranslation
								code={'exhibitor_partners'}
								translations={exhibition?.translations}
							>
								<Typography component={"div"} variant={"body1"}>
									<Trans
										i18nKey={'badge_info'}
										ns={EXHIBITOR_PROFILE}
									>
										On this page you can print the entry cards for you and your
										staff.<br/>
										Press the <strong>ADD PARTNER</strong> button or the <strong>IMPORT
										FROM
										EXCEL</strong> button.<br/><br/>
										<strong>Attention:</strong> For your smooth entrance to the
										exhibition,
										it is necessary to have the entrance card printed.<br/><br/> We
										would
										like to
										inform you that your Entrance card also serves as a <strong>Parking
										Pass</strong> in the Exhibition Centre parking areas.
									</Trans>
								</Typography>
							</CustomTranslation>
						</Paper>
						<Partners
							type={PartnerTypes.Partner}
							hideInvitedBy={!isOrganizer}
							exhibitorId={exhibitor.id}
							exhibitionId={exhibitionId}
							partners={exhibitor.partners.filter(f => Number(f.type) === PartnerTypes.Partner)}
						/>
					</DisplayPageIfAllowed>
				</TabPanel>
				<TabPanel isVisible={section === "serviceProviders"}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayServiceProvidersToExhibitor)}>
						<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
							<CustomTranslation
								code={'exhibitor_serviceProviders'}
								translations={exhibition?.translations}
							>
								<Typography component={"div"} variant={"body1"}>
									<Trans
										i18nKey={'badge_info_service_provider'}
										ns={EXHIBITOR_PROFILE}
									>
										On this page you can print the entry cards for your service
										providers.<br/>
										Press the <strong>ADD SERVICE PROVIDER</strong> button or
										the <strong>IMPORT
										FROM
										EXCEL</strong> button.<br/><br/>
										<strong>Attention:</strong> For your smooth entrance to the
										exhibition,
										it is necessary to have the entrance card with you.<br/>
									</Trans>
								</Typography>
							</CustomTranslation>
						</Paper>
						<Partners
							type={PartnerTypes.ServiceProvider}
							hideInvitedBy={!isOrganizer}
							exhibitorId={exhibitor.id}
							exhibitionId={exhibitionId}
							partners={exhibitor.partners.filter(f => Number(f.type) === PartnerTypes.ServiceProvider)}
							section={section}
						/>
					</DisplayPageIfAllowed>
				</TabPanel>
				<TabPanel isVisible={section === "contractors"}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayContractorsToExhibitor)}>
						<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
							<CustomTranslation
								code={'exhibitor_contractors'}
								translations={exhibition?.translations}
							>
								<Typography component={"div"} variant={"body1"}>
									<Trans
										i18nKey={'badge_info_contractor'}
										ns={EXHIBITOR_PROFILE}
									>
										On this page you can print the entry cards for your contractors.<br/>
										Press the <strong>ADD CONTRACTOR</strong> button or
										the <strong>IMPORT
										FROM
										EXCEL</strong> button.<br/><br/>
										<strong>Attention:</strong> For your smooth entrance to the
										exhibition,
										it is necessary to have the entrance card with you.<br/>
									</Trans>
								</Typography>
							</CustomTranslation>
						</Paper>
						<Partners
							type={PartnerTypes.Contractor}
							hideInvitedBy={!isOrganizer}
							exhibitorId={exhibitor.id}
							exhibitionId={exhibitionId}
							partners={exhibitor.partners.filter(f => Number(f.type) === PartnerTypes.Contractor)}
							section={section}
						/>
					</DisplayPageIfAllowed>
				</TabPanel>
				<PermissionHOC requiredPermissions={[Permissions.BeOrganizer]}>
					<TabPanel isVisible={section === "exhibitionStaff"}>
						<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
							<Typography component={"div"} variant={"body1"}>
								<Trans
									i18nKey={'badge_info_exhibition_staff'}
									ns={EXHIBITOR_PROFILE}
								>
									On this page you can print the entry cards for general exhibition
									staff.<br/>
									Press the <strong>ADD EXHIBITION STAFF</strong> button or
									the <strong>IMPORT FROM
									EXCEL</strong> button.<br/>
								</Trans>
							</Typography>
						</Paper>
						<Partners
							type={PartnerTypes.ExhibitionStaff}
							hideInvitedBy={!isOrganizer}
							exhibitorId={exhibitor.id}
							exhibitionId={exhibitionId}
							partners={exhibitor.partners.filter(f => Number(f.type) === PartnerTypes.ExhibitionStaff)}
						/>
					</TabPanel>
					<TabPanel isVisible={section === "organizers"}>
						<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
							<Typography component={"div"} variant={"body1"}>
								<Trans
									i18nKey={'badge_info_organizer'}
									ns={EXHIBITOR_PROFILE}
								>
									On this page you can print the entry cards for general exhibition
									staff.<br/>
									Press the <strong>ADD ORGANIZER</strong> button or
									the <strong>IMPORT FROM
									EXCEL</strong> button.<br/>
								</Trans>
							</Typography>
						</Paper>
						<Partners
							type={PartnerTypes.Organizer}
							hideInvitedBy={!isOrganizer}
							exhibitorId={exhibitor.id}
							exhibitionId={exhibitionId}
							partners={exhibitor.partners.filter(f => Number(f.type) === PartnerTypes.Organizer)}
						/>
					</TabPanel>
				</PermissionHOC>
				<TabPanel isVisible={section === "visitors"}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayVisitorsToExhibitor)}>
						<Box>
							<Paper sx={{ mb: 1, p: 1 }} variant={"outlined"}>
								<CustomTranslation
									code={'exhibitor_visitors'}
									translations={exhibition?.translations}
								>
									<Typography component={"div"} variant={"body1"}>
										<Trans
											i18nKey={'invitation_info'}
											ns={EXHIBITOR_PROFILE}
										>
											On this page you can send exhibition invitations by
											email. <br/><br/>
											Press <strong>ADD A VISITOR</strong> or <strong>IMPORT FROM
											EXCEL.</strong>
											<br/>
											After adding one or more visitors, press <strong>SEND INVITATION
											(NEW)</strong>, to send invitations by email to the newly added
											visitors.
											<br/>
											Alternatively, select specific visitors from the grid and
											press <strong>SEND
											INVITATION TO VISITORS</strong>, to send email invitations to
											selected
											visitors.
											<br/>
											<br/>
											<strong>Attention:</strong> Invitation concerns only commercial
											visitors..
										</Trans>
									</Typography>
								</CustomTranslation>
							</Paper>
							<Visitors
								limitedView={!isOrganizer}
								exhibitorId={exhibitor.id}
								exhibitionId={exhibitionId}
								visitors={exhibitor.visitors}
							/>
						</Box>
					</DisplayPageIfAllowed>
					{/*</Grid>*/}
					{/*TODO::Re-use if uploads per exhibitor are ever asked again*/}
					{/*<Grid size={{xs: 12, md: 4}}*/}
					{/*    <Paper sx={{px: 2, pb: 3}} variant={"outlined"}>*/}
					{/*        <ExhibitorFileUpload*/}
					{/*            exhibitorId={exhibitor.id}*/}
					{/*        />*/}
					{/*    </Paper>*/}
					{/*</Grid>*/}
					{/*</Grid>*/}
				</TabPanel>
				<TabPanel isVisible={section === "catalog"}>
					<Catalog
						exhibitionId={exhibitionId}
						exhibitor={exhibitor}
						loading={loading}
					/>
				</TabPanel>
				<TabPanel isVisible={section === "free-advertisement"}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayFreeAdvertisementToExhibitor)}>
						{exhibition ? (
							<FreeAdvertisement
								exhibition={exhibition}
								exhibitionId={exhibitionId}
								exhibitor={exhibitor}
								loading={loading}
							/>
						) : (
							<ContentLoader
								loading={loading}
							/>
						)}
						
					</DisplayPageIfAllowed>
				</TabPanel>
				<TabPanel isVisible={section === "kit"}>
					<DisplayPageIfAllowed
						allowed={isOrganizer || (isExhibitor && exhibition.displayExhibitorKitAndAddsToExhibitor)}>
						<Box sx={{ my: 1.5 }}/>
						<ExhibitorKit
							exhibitor={exhibitor}
						/>
					</DisplayPageIfAllowed>
				</TabPanel>
				<PermissionHOC requiredPermissions={[Permissions.BeExhibitor]}>
					<TabPanel isVisible={section === "scanVisitors"}>
						<DisplayPageIfAllowed
							allowed={isOrganizer || (isExhibitor && exhibition.hasExhibitorScanning && exhibitor?.hasExhibitorScanning)}>
							<Box sx={{ my: 1.5 }}/>
							<ScanVisitors
								exhibitionId={exhibitionId}
								exhibition={exhibition}
							/>
						</DisplayPageIfAllowed>
					</TabPanel>
				</PermissionHOC>
				<PermissionHOC requiredPermissions={[Permissions.BeExhibitor]}>
					<TabPanel isVisible={section === "myVisitors"}>
						<DisplayPageIfAllowed
							allowed={isOrganizer || (isExhibitor && exhibition.hasExhibitorScanning && exhibitor?.hasExhibitorScanning)}>
							<Box sx={{ my: 1.5 }}/>
							<ScannedVisitors exhibitor={exhibitor} exhibitionId={exhibitionId}/>
						</DisplayPageIfAllowed>
					</TabPanel>
				</PermissionHOC>
			</Grid>
			<ExhibitionUpdateStatus/>
		</Grid>
	)
}

const ExhibitorProfile = ({ id: propsId }) => {
	const { id: exhibitionId } = useParams()
	const queryParams = useQueryParams()
	const section = queryParams.get('subSection') ?? ""
	const { exhibitor, loading } = useExhibitor({
		language: 'any',
		overrideExhibitorId: propsId,
		section: section
	})
	return (
		<Fragment>
			{exhibitor ? (
				<WrapperWithSettingsButton id={exhibitionId}>
					<InnerContent
						section={section}
						propsId={propsId}
						exhibitionId={exhibitionId}
						exhibitor={exhibitor}
						loading={loading}
					/>
				</WrapperWithSettingsButton>
			) : (
				<ContentLoader
					loading={loading}
				/>
			)}
		</Fragment>
	)
}

export default ExhibitorProfile
